import { PropsWithChildren, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { Tabs } from '@mui/material';
import { styled } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Header, LwTab, ModalPanelTabLinkType, Page } from 'redesign';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { RouteDefinition, routes } from '../../../../Routes';

export const PartnerPage = ({ children }: PropsWithChildren<unknown>) => {
  const location = useLocation();
  const { allowPartnerRelations } = useFeatureFlagContext();
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const { id } = useParams() as { id: string };
  const { t } = useTranslation('hq-screen-specific');
  const selectedTabId = useMemo(() => {
    const path = location.pathname;
    return path.split('/').pop();
  }, [location.pathname]);
  const tabs: ModalPanelTabLinkType[] = useMemo(
    () => getTabs(allowPartnerRelations, generatePath, id, selectedTabId, t),
    [allowPartnerRelations, generatePath, id, selectedTabId, t]
  );

  return (
    <Page
      header={<Header titleText={t('partners.detail.page_title', { ns: 'hq-screen-specific' })} />}
      toolbox={
        <StyledTabs aria-label="partner-detail-tabs" value={selectedTabId}>
          {tabs.map((tab) => (
            <LwTab
              key={tab.id}
              value={tab.id}
              label={tab.tabLabel}
              onClick={() => {
                navigate(tab.to);
              }}
            />
          ))}
        </StyledTabs>
      }
    >
      {children}
    </Page>
  );
};

const getTabs = (
  allowPartnerRelations: boolean,
  generatePath: (
    route: RouteDefinition,
    params?:
      | {
          [key: string]: string | undefined;
        }
      | undefined
  ) => string,
  id: string,
  selectedTabId: string | undefined,
  t: TFunction<'hq-screen-specific', undefined>
) => {
  const tabs = [
    {
      to: generatePath(routes.PartnerDetail, { id }),
      id: 'info',
      tabLabel: t('partners.detail.tabs.info', { ns: 'hq-screen-specific' }),
    },
    ...(allowPartnerRelations
      ? [
          {
            to: generatePath(routes.PartnerRelation, { id }),
            id: 'relations',
            tabLabel: t('partners.detail.tabs.relations', { ns: 'hq-screen-specific' }),
          },
        ]
      : []),
    {
      to: generatePath(routes.PartnerUsers, { id }),
      id: 'users',
      tabLabel: t('partners.detail.tabs.users', { ns: 'hq-screen-specific' }),
    },
  ];
  return tabs.map((tab) => ({
    ...tab,
    selected: tab.id === selectedTabId,
  }));
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& div.MuiTabs-scroller': {
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(8),
      overflow: 'visible',
    },
  },
}));
